import { X } from 'lucide-react';
import type { FiltersConfig } from '@rocco/ui/filter-sort/types/filter-config-section';
import { getRefineChips } from '@rocco/ui/filter-sort/components/filters-modal/refine/get-refine-chips';
import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type {
    FilterStates,
    FilterStatesKey,
} from '@rocco/ui/filter-sort/view-models/filter-states';

type UpdateFilterState = (
    filterKey: FilterStatesKey,
    newState: FilterState,
    shouldAggregate?: boolean,
) => void;

interface RefineChipsRowProps {
    filterStates: FilterStates;
    config: FiltersConfig;
    onUpdateFilterState: UpdateFilterState;
    onUpdateFacetFilterState: UpdateFilterState;
    onClearFilters: () => void;
}

/**
 * One chip per staged selection with per-chip removal, plus "Clear all".
 * Applied state stays legible at the top of the sheet instead of hiding
 * behind closed sections; removing a chip re-runs the live count.
 */
export const RefineChipsRow = (props: RefineChipsRowProps) => {
    const {
        filterStates,
        config,
        onUpdateFilterState,
        onUpdateFacetFilterState,
        onClearFilters,
    } = props;

    const chips = getRefineChips(filterStates, config);

    if (chips.length === 0) return null;

    return (
        <section aria-label="Selected filters" className="flex flex-col">
            <div className="flex min-h-11 items-center justify-between gap-2">
                <div className="text-base font-medium">Selected filters</div>
                <button
                    type="button"
                    onClick={onClearFilters}
                    className="shrink-0 cursor-pointer text-sm text-gray-500 underline-offset-2 hover:underline"
                >
                    Clear all
                </button>
            </div>
            <div className="-mx-6 flex scrollbar-hidden gap-2 overflow-x-auto px-6 pb-2">
                {chips.map((chip) => (
                    <button
                        key={chip.id}
                        type="button"
                        aria-label={`Remove filter ${chip.label}`}
                        onClick={() =>
                            (chip.isFacet
                                ? onUpdateFacetFilterState
                                : onUpdateFilterState)(
                                chip.filterKey,
                                chip.removedState,
                                true,
                            )
                        }
                        className="flex min-h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-black px-3 py-1.5 text-sm text-white"
                    >
                        {chip.label}
                        <X aria-hidden className="size-3.5" />
                    </button>
                ))}
            </div>
        </section>
    );
};
